import { shallowEqual, Selector, useSelector } from 'react-redux';
import identity from 'lodash-es/identity';
import { FC, ReactNode } from 'react';

import useAppSelector from './selectors/useAppSelector';
import useGlobalAppSelector from './selectors/useGlobalAppSelector';
import useCombinedSelector from './selectors/useCombinedSelector';
import { CombinedSelector } from './selectors/types';

export const CombinedStateConsumer = <AppState, RootState, Result>({
  children,
  selector,
}: {
  children: (result: Result) => ReactNode;
  selector: CombinedSelector<AppState, RootState, Result>;
}) => {
  const state = useCombinedSelector(selector);

  return children(state);
};

const createStateConsumer =
  (useSelectorHook: typeof useSelector) =>
  <T, P>({
    children,
    selector = identity,
  }: {
    children:
      | ((result: ReturnType<Selector<T, P>>) => ReactNode)
      | FC<ReturnType<Selector<T, P>>>;
    selector: Selector<T, P>;
  }) => {
    const state = useSelectorHook(selector, shallowEqual);

    return children(state);
  };

export const StateConsumer = createStateConsumer(useAppSelector);
export const RootStateConsumer = createStateConsumer(useSelector);
export const GlobalStateConsumer = createStateConsumer(useGlobalAppSelector);
